<template>
  <div>
    <transition name="fade" mode="out-in" duration="100">
      <LoadingCircle v-if="isLoading" class="tw-mx-auto" />
      <Avatar v-else class="tw-mx-auto" :src="avatarSrc" :size="size" />
    </transition>
    <div class="tw-mt-4 tw-text-center">
      <h3 class="tw-mb-1 tw-font-semibold">Change profile photo</h3>
      <p class="tw-text-xs tw-text-neutral-400">
        Image must be
        <strong class="tw-font-semibold">.jpg</strong>
        or
        <strong class="tw-font-semibold">.png</strong>
        with minimum size of 160x160 pixels.
      </p>
      <p v-if="isError" class="tw-text-sm tw-text-error-500">
        Something went wrong while uploading your avatar.
      </p>
    </div>
    <slot
      name="buttons"
      :is-loading="isLoading"
      :is-avatar-set="isAvatarSet"
      :upload-new-image="{ activator: openCropper }"
      :delete-existing-image="{
        activator: deleteAvatar,
        confirmationText,
      }"
    />

    <VueAvatarCropper
      v-model="isCropperShown"
      upload-url="/avatar"
      :request-options="{
        method: 'PATCH',
        headers: { 'X-CSRF-TOKEN': csrfToken },
      }"
      upload-file-field="avatar"
      :output-options="{ width: 300, height: 300 }"
      @uploading="setIsLoading(true)"
      @uploaded="handleUploaded"
      @error="setIsError(true)"
      mimes="image/png, image/jpeg"
      :labels="{
        submit: 'Save',
        cancel: 'Cancel',
      }"
    />
  </div>
</template>

<script setup>
import VueAvatarCropper from 'vue-avatar-cropper'
import Avatar from '@/shared/components/avatar/Avatar.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import axios from 'axios'
import { computed, ref } from 'vue'

const emit = defineEmits(['success'])

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
  },
  isSet: {
    type: Boolean,
    default: false,
  },
})

const confirmationText = ref(null)
const confirmationTimeout = ref(null)
const avatarSrc = ref(props.src)
const isAvatarSet = ref(props.isSet)
const isCropperShown = ref(false)
const isLoading = ref(false)
const isError = ref(false)
const confirmDeleteAvatar = ref(false)

const csrfToken = computed(() => {
  return document.head.querySelector('[name=csrf-token]').content
})

const handleUploaded = async ({ response }) => {
  if (!response.ok) return
  isError.value = false
  const { url } = await response.json()
  avatarSrc.value = url
  isAvatarSet.value = true
  isLoading.value = false
  emit('success')
}

const deleteAvatar = async () => {
  isError.value = false
  if (!confirmDeleteAvatar.value) {
    confirmationText.value = 'Are you sure?'
    confirmDeleteAvatar.value = true
    confirmationTimeout.value = setTimeout(() => {
      resetDeleteButton()
    }, 3000)
  } else {
    clearTimeout(confirmationTimeout.value)
    isLoading.value = true
    const {
      data: { url },
    } = await axios.delete('/avatar')
    isLoading.value = false
    avatarSrc.value = url
    isAvatarSet.value = false
    resetDeleteButton()
    emit('success')
  }
}

const setIsLoading = (value) => {
  isLoading.value = value
}

const setIsError = (value) => {
  isError.value = value
}

const openCropper = () => {
  isCropperShown.value = true
}

const resetDeleteButton = () => {
  confirmDeleteAvatar.value = false
  confirmationText.value = null
}
</script>
